import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL, tokenStore } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Receipt, Download, ExternalLink, Loader2, ChevronRight, Search,
} from "lucide-react";
import { toast } from "sonner";

const MONTHS_NO = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

const CURRENCY_SYM = { EUR: "€", NOK: "kr" };

function fmt(n, cur) {
  return `${(n || 0).toLocaleString("nb-NO", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} ${CURRENCY_SYM[cur] || cur}`;
}

export default function ReceiptsArchivePage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(""); // empty = all months
  const [boatId, setBoatId] = useState("");
  const [q, setQ] = useState("");
  const [receipts, setReceipts] = useState([]);
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (month) params.month = Number(month);
      if (boatId) params.boat_id = boatId;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/receipts", { params });
      setReceipts(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste kvitteringer");
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  };
  const loadBoats = async () => {
    try {
      const r = await api.get("/boats");
      setBoats(r.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadBoats(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, boatId]);

  const totals = useMemo(() => {
    const acc = {};
    for (const r of receipts) {
      for (const [cur, t] of Object.entries(r.totals || {})) {
        const a = acc[cur] || { gross: 0, net: 0, vat: 0, receipts: 0 };
        a.gross += t.gross || 0;
        a.net += t.net || 0;
        a.vat += t.vat || 0;
        a.receipts += 1;
        acc[cur] = a;
      }
    }
    return acc;
  }, [receipts]);

  const yearsAvailable = useMemo(() => {
    const yrs = new Set([now.getFullYear()]);
    for (let i = 1; i <= 4; i += 1) yrs.add(now.getFullYear() - i);
    return Array.from(yrs).sort((a, b) => b - a);
  }, [now]);

  const csvUrl = useMemo(() => {
    const params = new URLSearchParams({
      year: String(year),
      auth: tokenStore.get() || "",
    });
    if (month) params.set("month", String(month));
    if (boatId) params.set("boat_id", boatId);
    if (q.trim()) params.set("q", q.trim());
    return `${BACKEND_URL}/api/receipts/export.csv?${params}`;
  }, [year, month, boatId, q]);

  const pdfUrl = (id) =>
    `${BACKEND_URL}/api/receipts/${id}/pdf?auth=${encodeURIComponent(tokenStore.get() || "")}`;

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="receipts-archive-page">
      <div>
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.32em] text-[#A57F39]">
          Regnskap
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">
          Kvitteringsarkiv
        </h1>
        <p className="text-xs sm:text-sm text-[#2B3F4D] mt-1 max-w-2xl">
          Alle godkjente kvitteringer med kunde, båt, MVA-splitt og total.
          Bruk filtrene for å avgrense og eksporter til CSV som regnskapsføreren
          din kan åpne direkte i Excel.
        </p>
      </div>

      {/* Filters */}
      <Card className="p-3 sm:p-4">
        <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_2fr_auto] gap-2 sm:gap-3 items-end">
          <div>
            <Label className="text-xs">År</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="receipts-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearsAvailable.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Måned</Label>
            <Select value={month || "__all__"} onValueChange={(v) => setMonth(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="receipts-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Alle måneder</SelectItem>
                {MONTHS_NO.map((m, i) => (
                  <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Båt</Label>
            <Select value={boatId || "__all__"} onValueChange={(v) => setBoatId(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="receipts-boat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Alle båter</SelectItem>
                {boats.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Label className="text-xs">Søk (nummer, kunde, e-post)</Label>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                data-testid="receipts-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                onBlur={load}
                placeholder="f.eks. 2026-0001"
                className="pl-8"
              />
            </div>
          </div>
          <Button
            asChild
            data-testid="receipts-export-csv"
            className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white col-span-2 sm:col-auto"
            disabled={receipts.length === 0}
          >
            <a href={csvUrl} download>
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </a>
          </Button>
        </div>
      </Card>

      {/* Totals summary */}
      {Object.keys(totals).length > 0 && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3"
          data-testid="receipts-totals"
        >
          {Object.entries(totals).map(([cur, t]) => (
            <Card
              key={cur}
              className="p-4 bg-gradient-to-br from-[#0C1824] to-[#11283A] text-white"
              data-testid={`receipts-total-${cur}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
                  Sum {cur}
                </div>
                <Badge className="bg-white/10 border-0 text-white/80 text-[10px]">
                  {t.receipts} kvittering{t.receipts === 1 ? "" : "er"}
                </Badge>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] uppercase text-white/50">Netto</div>
                  <div className="font-mono font-semibold text-sm mt-0.5">{fmt(t.net, cur)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-white/50">MVA</div>
                  <div className="font-mono font-semibold text-sm mt-0.5 text-[#ECC974]">{fmt(t.vat, cur)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-white/50">Brutto</div>
                  <div className="font-mono font-bold text-base mt-0.5">{fmt(t.gross, cur)}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Receipts list */}
      {loading ? (
        <Card className="p-10 flex items-center justify-center text-[#A57F39]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Laster…
        </Card>
      ) : receipts.length === 0 ? (
        <Card className="p-8 text-center" data-testid="receipts-empty">
          <Receipt className="w-8 h-8 text-[#BD984D]/60 mx-auto mb-2" />
          <div className="text-sm text-[#2B3F4D]">
            Ingen godkjente kvitteringer funnet for de valgte filtrene.
          </div>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="receipts-list">
          {receipts.map((r) => (
            <Card
              key={r.id}
              className="p-3 sm:p-4 hover:border-[#BD984D]/50 transition-colors"
              data-testid={`receipt-row-${r.receipt_number}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#0C1824] text-white border-0 font-mono text-xs">
                      {r.receipt_number}
                    </Badge>
                    <span className="text-xs text-[#2B3F4D]">
                      {(r.issued_at || "").slice(0, 10)}
                    </span>
                    {r.boat_name && (
                      <Badge className="bg-[#BD984D]/15 text-[#A57F39] border-0 text-[10px]">
                        {r.boat_name}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1.5 font-semibold text-[#0C1824] truncate text-sm sm:text-base">
                    {r.customer_name || r.customer_email || "—"}
                  </div>
                  <div className="text-[11px] text-[#2B3F4D] mt-0.5">
                    {r.booking_start_date && r.booking_end_date && (
                      <>Tur {r.booking_start_date} → {r.booking_end_date}</>
                    )}
                    {r.issued_by_name && <> · utstedt av {r.issued_by_name}</>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {Object.entries(r.totals || {}).map(([cur, t]) => (
                    <div
                      key={cur}
                      className="font-mono font-bold text-[#0C1824] tabular-nums text-sm sm:text-base"
                    >
                      {fmt(t.gross, cur)}
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  data-testid={`receipt-pdf-${r.receipt_number}`}
                  className="border-[#0C1824] text-[#0C1824]"
                >
                  <a href={pdfUrl(r.id)} target="_blank" rel="noreferrer">
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Åpne PDF
                  </a>
                </Button>
                <Button
                  asChild
                  size="sm"
                  variant="ghost"
                  data-testid={`receipt-open-${r.receipt_number}`}
                  className="text-[#A57F39]"
                >
                  <Link to={`/admin/bookings/${r.booking_id}`}>
                    Booking <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
                {r.drive_view_url && (
                  <Button
                    asChild
                    size="sm"
                    variant="ghost"
                    className="text-[#1DA3B3]"
                  >
                    <a href={r.drive_view_url} target="_blank" rel="noreferrer">
                      Drive <ExternalLink className="w-3.5 h-3.5 ml-1" />
                    </a>
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
